import type { EbookQuestion } from '@wx-weiqi/contracts';
import { removeAnswerBranch } from './ebook-editing';

export type EbookEditorInputState = { answer: boolean; busy: boolean; modal: boolean };
export function ebookEditorInputAllowed(state: EbookEditorInputState) {
  return state.answer && !state.busy && !state.modal;
}

/** Browsing never edits the answer tree; undefined means no permitted navigation. */
export function navigateEbookAnswer(
  question: EbookQuestion,
  selected: string | null,
  remembered: ReadonlyMap<string | null, string>,
  direction: 'FIRST' | 'PREVIOUS' | 'NEXT' | 'LAST',
  input: EbookEditorInputState,
): string | null | undefined {
  if (!ebookEditorInputAllowed(input)) return undefined;
  const current = question.nodes.find((node) => node.id === selected);
  if (direction === 'FIRST') return current ? null : undefined;
  if (direction === 'PREVIOUS') return current ? current.parentId : undefined;
  let parent = selected;
  const seen = new Set<string>();
  do {
    const options = question.nodes.filter((node) => node.parentId === parent);
    const next = options.find((node) => node.id === remembered.get(parent)) ?? options[0];
    if (!next || seen.has(next.id)) break;
    seen.add(next.id);
    parent = next.id;
  } while (direction === 'LAST');
  return parent === selected ? undefined : parent;
}

export type DeletedEbookAnswerBranch = {
  questionId: string;
  position: string;
  nodes: EbookQuestion['nodes'];
  originalOrder: string[];
  parentId: string | null;
  selectedId: string;
};

export const ebookAnswerPositionKey = (question: EbookQuestion) =>
  JSON.stringify([question.size, question.currentPlayer, question.stones]);

export function deleteEbookAnswerBranch(
  question: EbookQuestion,
  selected: string | null,
  input: EbookEditorInputState,
) {
  if (!ebookEditorInputAllowed(input)) return null;
  const current = question.nodes.find((node) => node.id === selected);
  if (!current) return null;
  const next = removeAnswerBranch(question, current.id);
  const retained = new Set(next.nodes.map((node) => node.id));
  const deleted: DeletedEbookAnswerBranch = {
    questionId: question.id,
    position: ebookAnswerPositionKey(question),
    nodes: question.nodes.filter((node) => !retained.has(node.id)),
    originalOrder: question.nodes.map((node) => node.id),
    parentId: current.parentId,
    selectedId: current.id,
  };
  return {
    question: next,
    selected: current.parentId,
    deleted,
  };
}

/** Restore only deleted nodes so saving or later editing never rolls back unrelated content. */
export function restoreEbookAnswerBranch(
  question: EbookQuestion,
  deleted: DeletedEbookAnswerBranch,
  input: EbookEditorInputState,
) {
  if (!ebookEditorInputAllowed(input)) return null;
  if (question.id !== deleted.questionId || ebookAnswerPositionKey(question) !== deleted.position)
    throw new Error('初始棋形已改变，无法恢复原局面的删除。');
  const parent = question.nodes.find((node) => node.id === deleted.parentId);
  if (deleted.parentId !== null && !parent) throw new Error('上级变化已删除，请先恢复上级变化。');
  if (parent && parent.outcome !== 'CONTINUE')
    throw new Error('上级变化已结束，请先将它改为“继续作答”再恢复。');
  if (question.nodes.length + deleted.nodes.length > 1000)
    throw new Error('恢复后超出答案节点数量上限，请先减少变化。');
  const restoredRoot = deleted.nodes.find((node) => node.id === deleted.selectedId);
  if (
    question.nodes.some(
      (node) =>
        deleted.nodes.some((removed) => removed.id === node.id) ||
        (node.parentId === deleted.parentId &&
          restoredRoot &&
          JSON.stringify(node.move) === JSON.stringify(restoredRoot.move)),
    )
  )
    throw new Error('此处已有相同着法，请先处理新录入的变化后再恢复。');
  const nodes = [...question.nodes];
  // Reinsert before the next surviving original node; new nodes and edits stay intact.
  for (const node of [...deleted.nodes].reverse()) {
    const originalIndex = deleted.originalOrder.indexOf(node.id);
    const following = new Set(deleted.originalOrder.slice(originalIndex + 1));
    const index = nodes.findIndex((item) => following.has(item.id));
    nodes.splice(index < 0 ? nodes.length : index, 0, node);
  }
  return { ...question, nodes, answerConfirmed: false };
}

export type EbookWheelState = { total: number; lastEvent: number; lastStep: number };
export const initialEbookWheelState = (): EbookWheelState => ({
  total: 0,
  lastEvent: -Infinity,
  lastStep: -Infinity,
});
export type EbookWheelInput = {
  deltaX: number;
  deltaY: number;
  deltaMode: number;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
};

/** Normalize wheel units, accumulate trackpad movement, and emit at most one step per 240ms. */
export function ebookWheelNavigation(
  state: EbookWheelState,
  input: EbookWheelInput,
  now: number,
  allowed: boolean,
) {
  const reset = { ...state, total: 0, lastEvent: now };
  if (
    !allowed ||
    input.ctrlKey ||
    input.metaKey ||
    input.shiftKey ||
    !Number.isFinite(input.deltaY) ||
    !Number.isFinite(input.deltaX) ||
    input.deltaY === 0 ||
    Math.abs(input.deltaX) >= Math.abs(input.deltaY)
  )
    return { state: reset, direction: null, handled: false } as const;
  const delta = input.deltaY * (input.deltaMode === 1 ? 16 : input.deltaMode === 2 ? 400 : 1);
  if (now - state.lastStep < 240) return { state: reset, direction: null, handled: true } as const;
  const prior =
    now - state.lastEvent > 180 || Math.sign(state.total) !== Math.sign(delta) ? 0 : state.total;
  const total = prior + delta;
  if (Math.abs(total) < 40)
    return { state: { ...state, total, lastEvent: now }, direction: null, handled: true } as const;
  return {
    state: { total: 0, lastEvent: now, lastStep: now },
    direction: total < 0 ? 'PREVIOUS' : 'NEXT',
    handled: true,
  } as const;
}
